/**
 * useLiveBallTracker - a thin, self-healing client for the Bzzoiro live football
 * WebSocket that drives the {@link LiveBallPitch}. It mints a short-lived socket
 * credential (cached via React Query), opens the socket, subscribes to one
 * `event_id`, keeps it alive, parses the documented frame types into a single
 * coalesced UI state object, and silently ignores anything undocumented.
 *
 * Design notes:
 *  - The socket, timers and "latest" scratch values live in REFS, never in
 *    state, so a 5-second `livedata` tick mutates one `ball` state object and
 *    does NOT re-render unrelated subtrees.
 *  - Every setState is guarded by a `mounted` ref so a frame that lands after
 *    unmount / disable can never warn ("setState on unmounted").
 *  - On an unexpected close while still enabled, we reconnect with exponential
 *    backoff (1s,2s,4s,8s … capped 30s). A clean teardown (unmount/disable) does
 *    NOT reconnect.
 *  - Secrets discipline: the token/url come only from the edge fn and are never
 *    logged.
 */
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { isSupabaseConfigured, QUERY_STALE_TIME } from '@/config';
import { getBzzoiroWsToken } from '@/lib/data/provider';
import { queryKeys } from '@/lib/data/keys';
import { notifyGoal, notifyRedCard } from '@/lib/notifications/liveEvents';

// ---------------------------------------------------------------------------
// Public shapes
// ---------------------------------------------------------------------------

export type LiveBallStatus =
  | 'idle'
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'not_tracked'
  | 'error';

/**
 * The situation tags the feed emits. Includes BOTH the documented set AND the
 * REAL values observed live on a full-source match (safe / attack /
 * dangerous_attack / corner / shotoffwoodwork). Free-form default kept so an
 * unforeseen tag never breaks the UI (it maps to neutral "open play").
 */
export type BallSituation =
  // documented
  | 'regular_play'
  | 'free_kick'
  | 'corner_kick'
  | 'goal_kick'
  | 'throw_in'
  | 'penalty'
  | 'kick_off'
  | 'goal'
  // real, observed live
  | 'safe'
  | 'attack'
  | 'dangerous_attack'
  | 'corner'
  | 'shotoffwoodwork'
  | (string & {});

/**
 * The danger temperature a situation maps to. Drives the center-overlay color,
 * the vertical position-indicator tint and the territory shade in the 365-style
 * pitch — `low` is calm, `medium` an amber attack, `high` a red danger moment.
 */
export type DangerLevel = 'low' | 'medium' | 'high';

/** Which side currently has the ball (drives the 365 possession headline). */
export type PossessionSide = 'home' | 'away' | null;

export interface BallPoint {
  /** 0..100, LEFT → RIGHT. */
  x: number;
  /** 0..100, BOTTOM → TOP. */
  y: number;
}

export interface LiveBall extends BallPoint {
  situation: BallSituation;
  /** Calm→hot temperature derived from `situation`; drives every danger tint. */
  danger: DangerLevel;
  /** The team in possession when this frame was emitted (365 headline), or null. */
  side: PossessionSide;
  /** Short live English commentary for this frame ("Into the box!"), or null. */
  commentary: string | null;
}

/** A team-level live stat pair (possession is a 0..100 percentage). */
export interface LiveSidePair {
  home: number;
  away: number;
}

export interface LiveBallStats {
  shots: LiveSidePair;
  shots_on_target: LiveSidePair;
  xg: LiveSidePair;
  possession: LiveSidePair;
}

export interface LiveTime {
  minute: number | null;
  /**
   * Normalized half/period label as a STRING ("1H"/"2H"/…). The wire may send
   * either an int (1/2) or a string ("1H"/"2H"); {@link readTime} coerces both.
   */
  period: string | null;
  status: string | null;
  /** The provider's pre-formatted "min:sec" clock ("24:14") when present. */
  display: string | null;
}

/** A normalized FULL-feed action used for the celebratory pulse + ticker line. */
export interface LiveAction {
  id: string;
  action_type: string;
  team: 'home' | 'away' | null;
  player: string | null;
  x: number | null;
  y: number | null;
  minute: number | null;
  period: string | null;
}

/**
 * A celebratory in-app event the pitch flashes briefly. Emitted ONLY on a real
 * later increment / red card (never on the initial snapshot or a replayed
 * frame). The `id` is monotonic so the overlay re-triggers even on two goals
 * with the same scoreline (e.g. an own-goal correction back to the same score).
 */
export interface LiveGoalEvent {
  id: number;
  kind: 'goal' | 'red_card';
  /** Which side scored / was sent off, when known. */
  team: 'home' | 'away' | null;
  /** The scorer (goal) or the sent-off player (red card), when known. */
  player: string | null;
}

export interface LiveBallState {
  status: LiveBallStatus;
  source: 'basic' | 'full' | null;
  ball: LiveBall | null;
  /** Recent ball positions, NEWEST first, capped at TRAIL_CAP. */
  trail: BallPoint[];
  score: { home: number; away: number };
  time: LiveTime;
  stats: LiveBallStats;
  /** The most recent FULL action (drives the pulse), or null. */
  lastAction: LiveAction | null;
  /** Recent FULL actions, NEWEST first, capped at ACTIONS_CAP. */
  actions: LiveAction[];
  /**
   * The latest real goal / red-card moment for the celebratory pitch flash, or
   * null. Bumped only on a genuine later increment / red card — the LiveBallPitch
   * self-dismisses the flash ~2s after the `id` changes.
   */
  goalEvent: LiveGoalEvent | null;
}

// ---------------------------------------------------------------------------
// Tunables
// ---------------------------------------------------------------------------

const TRAIL_CAP = 6;
const ACTIONS_CAP = 20;
// Keep the ping under common 30s proxy idle timeouts so the socket is not
// silently dropped by an intermediary mid-match.
const PING_INTERVAL_MS = 27_000;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
// Stop retrying a permanently-down feed so we don't drain battery/radio with an
// endless 30s retry storm; surface the graceful error UI instead.
const MAX_RECONNECT_ATTEMPTS = 8;
// If two consecutive ping ticks pass with no inbound frame, treat the socket as
// dead and force a reconnect (catches half-open "zombie" sockets).
const DEAD_FRAME_TICKS = 2;

const EMPTY_PAIR: LiveSidePair = { home: 0, away: 0 };
const EMPTY_STATS: LiveBallStats = {
  shots: EMPTY_PAIR,
  shots_on_target: EMPTY_PAIR,
  xg: EMPTY_PAIR,
  possession: EMPTY_PAIR,
};

const INITIAL_STATE: LiveBallState = {
  status: 'idle',
  source: null,
  ball: null,
  trail: [],
  score: { home: 0, away: 0 },
  time: { minute: null, period: null, status: null, display: null },
  stats: EMPTY_STATS,
  lastAction: null,
  actions: [],
  goalEvent: null,
};

// ---------------------------------------------------------------------------
// Frame parsing helpers (defensive: the wire is JSON-from-a-server)
// ---------------------------------------------------------------------------

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  // The wire occasionally sends numeric-looking strings (e.g. minute "23"):
  // accept those too so an int-as-string never reads as null.
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Map a raw `situation` tag → its danger temperature. Covers BOTH the documented
 * tags and the REAL live values. Anything unrecognized is treated as calm open
 * play ('low') so a new tag degrades gracefully instead of flashing red.
 */
const SITUATION_DANGER: Record<string, DangerLevel> = {
  // calm / open play
  safe: 'low',
  regular_play: 'low',
  kick_off: 'low',
  goal_kick: 'low',
  // building attacks → amber
  attack: 'medium',
  free_kick: 'medium',
  throw_in: 'medium',
  corner: 'medium',
  corner_kick: 'medium',
  shotoffwoodwork: 'medium',
  // hot moments → red
  dangerous_attack: 'high',
  penalty: 'high',
  goal: 'high',
};

function dangerForSituation(situation: string): DangerLevel {
  return SITUATION_DANGER[situation] ?? 'low';
}

/**
 * The ONLY action_type values worth surfacing in the ticker + pulse. The full
 * feed streams routine actions (pass / ball_touch / ball_recovery / clearance /
 * unknown_NN) far too frequently to show — those are ignored here.
 */
const MEANINGFUL_ACTIONS = new Set<string>([
  'goal',
  'shot',
  'save',
  'yellow_card',
  'red_card',
  'corner_kick',
  'corner',
  'penalty',
  'post',
  'shotoffwoodwork',
]);

/**
 * Normalize a period that may arrive as an int (1/2) OR a string ("1H"/"2H"/
 * "1st_half") into a stable short label ("1H"/"2H"/…). Returns null when absent.
 */
function readPeriod(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return `${value}H`;
  if (typeof value === 'string' && value.trim() !== '') {
    // Already a short label ("1H") — keep as-is; otherwise pull a leading digit
    // out of a verbose status ("1st_half" → "1H") and fall back to the raw text.
    if (/^[0-9]+H$/i.test(value)) return value.toUpperCase();
    const digit = value.match(/[0-9]+/);
    return digit ? `${digit[0]}H` : value;
  }
  return null;
}

function clamp01to100(value: number): number {
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}

/**
 * A side's stat block from an `event` frame → our normalized pair fields.
 *
 * Tolerant of BOTH the documented keys (shots / xg / possession) and the REAL
 * live keys (total_shots / expected_goals / ball_possession): the first present
 * key on each side wins, so either wire shape populates the strip.
 */
function readStats(eventStats: unknown): LiveBallStats {
  if (!eventStats || typeof eventStats !== 'object') return EMPTY_STATS;
  const s = eventStats as { home?: Record<string, unknown>; away?: Record<string, unknown> };
  const home = s.home ?? {};
  const away = s.away ?? {};
  // First defined numeric value across the candidate keys (real OR documented).
  const pick = (block: Record<string, unknown>, keys: string[]): number => {
    for (const key of keys) {
      const n = asNumber(block[key]);
      if (n !== null) return Math.max(n, 0);
    }
    return 0;
  };
  const pair = (keys: string[]): LiveSidePair => ({
    home: pick(home, keys),
    away: pick(away, keys),
  });
  return {
    shots: pair(['total_shots', 'shots']),
    shots_on_target: pair(['shots_on_target']),
    xg: pair(['expected_goals', 'xg']),
    possession: pair(['ball_possession', 'possession']),
  };
}

function readScore(frame: { score?: { home?: number | null; away?: number | null } }): {
  home: number;
  away: number;
} {
  return {
    home: Math.max(asNumber(frame.score?.home) ?? 0, 0),
    away: Math.max(asNumber(frame.score?.away) ?? 0, 0),
  };
}

function readTime(frame: { time?: Record<string, unknown> }): LiveTime {
  const time = frame.time ?? {};
  return {
    minute: asNumber(time.minute),
    // period may be an int (1/2) OR a string ("1H"/"1st_half") — coerce both.
    period: readPeriod(time.period),
    status: typeof time.status === 'string' ? time.status : null,
    display: typeof time.display === 'string' && time.display.trim() !== '' ? time.display : null,
  };
}

/** Pull an {x,y} point out of one `coordinates` array element. */
function readCoordinatePoint(raw: unknown): BallPoint | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as { x?: unknown; y?: unknown };
  const x = asNumber(p.x);
  const y = asNumber(p.y);
  if (x === null || y === null) return null;
  return { x: clamp01to100(x), y: clamp01to100(y) };
}

/**
 * Resolve the CURRENT ball position from a livedata frame.
 *
 * REAL full-source frames carry the ball inside `coordinates` (the LAST element
 * is the current ball; earlier elements are a short trajectory). Basic-source
 * frames may instead use a flat top-level `{x,y}`. We try `coordinates` first,
 * then fall back to the flat shape — this is the core fix: the old parser only
 * read `frame.x`/`frame.y`, so on full data the ball never moved.
 */
function readBall(frame: Record<string, unknown>): LiveBall | null {
  let point: BallPoint | null = null;

  const coords = frame.coordinates;
  if (Array.isArray(coords) && coords.length > 0) {
    point = readCoordinatePoint(coords[coords.length - 1]);
  }
  if (!point) {
    // Basic-source fallback: flat top-level {x,y}.
    point = readCoordinatePoint(frame);
  }
  if (!point) return null;

  const situation = typeof frame.situation === 'string' ? frame.situation : 'regular_play';
  const side =
    frame.side === 'home' || frame.side === 'away' ? (frame.side as PossessionSide) : null;
  const commentary =
    typeof frame.commentary === 'string' && frame.commentary.trim() !== ''
      ? frame.commentary.trim()
      : null;

  return {
    x: point.x,
    y: point.y,
    situation,
    danger: dangerForSituation(situation),
    side,
    commentary,
  };
}

/**
 * The earlier `coordinates` points (everything BEFORE the last) as a short
 * trajectory, NEWEST first, so the trail can be seeded from a single frame.
 * Empty for flat/basic frames that carry no trajectory.
 */
function readBallTrajectory(frame: Record<string, unknown>): BallPoint[] {
  const coords = frame.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return [];
  const out: BallPoint[] = [];
  // Skip the last element (that is the current ball); walk backwards so the
  // point nearest the ball is the freshest trail node.
  for (let i = coords.length - 2; i >= 0 && out.length < TRAIL_CAP; i -= 1) {
    const p = readCoordinatePoint(coords[i]);
    if (p) out.push(p);
  }
  return out;
}

function readAction(frame: Record<string, unknown>): LiveAction | null {
  const actionType = typeof frame.action_type === 'string' ? frame.action_type : null;
  if (!actionType) return null;
  // The full feed streams routine actions (pass / ball_touch / ball_recovery /
  // clearance / unknown_NN) extremely often — only surface meaningful ones.
  if (!MEANINGFUL_ACTIONS.has(actionType)) return null;
  const player = frame.player as { name?: unknown } | undefined;
  const team = frame.team === 'home' || frame.team === 'away' ? frame.team : null;
  // tid identifies the team, not the action — combine with second (when present)
  // so two same-minute actions don't collide on one id.
  const id = `${asNumber(frame.tid) ?? 0}-${actionType}-${asNumber(frame.minute) ?? 0}-${
    asNumber(frame.second) ?? Date.now()
  }`;
  return {
    id,
    action_type: actionType,
    team,
    player: typeof player?.name === 'string' ? player.name : null,
    x: asNumber(frame.x),
    y: asNumber(frame.y),
    minute: asNumber(frame.minute),
    period: readPeriod(frame.period),
  };
}

const ERROR_CODE_TO_STATUS: Record<string, LiveBallStatus> = {
  not_tracked: 'not_tracked',
  subscription_required: 'error',
  auth_failed: 'error',
  limit: 'error',
  bad_action: 'error',
  bad_event_id: 'error',
};

// ---------------------------------------------------------------------------
// Token hook
// ---------------------------------------------------------------------------

function useBzzoiroWsToken(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.bzzoiroWsToken,
    queryFn: getBzzoiroWsToken,
    enabled: isSupabaseConfigured && enabled,
    // The minted credential is valid ~1h; refresh comfortably inside that window.
    staleTime: QUERY_STALE_TIME.reference,
    gcTime: 60 * 60 * 1000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

export function useLiveBallTracker({
  eventId,
  enabled,
  homeShortName,
  awayShortName,
  fixtureId,
}: {
  eventId: number | null;
  enabled: boolean;
  /** Short team labels used in the goal alert body; optional (a fallback is used). */
  homeShortName?: string;
  awayShortName?: string;
  /**
   * The fixture (route) UUID, threaded into the live goal / red-card alert so a
   * tap deep-links to /match/<id>. Optional — without it the alert still fires,
   * it just isn't tappable to the match.
   */
  fixtureId?: string | null;
}): LiveBallState {
  const active = enabled && eventId !== null && Number.isFinite(eventId);
  const tokenQuery = useBzzoiroWsToken(active);
  const token = tokenQuery.data ?? null;

  const [state, setState] = useState<LiveBallState>(INITIAL_STATE);

  // Refs that survive renders and never trigger one.
  const mountedRef = useRef(true);
  const socketRef = useRef<WebSocket | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  // The scratch trail/actions buffers: mutated on each tick, then spread into
  // one coalesced state update so a livedata tick re-renders only the pitch.
  const trailRef = useRef<BallPoint[]>([]);
  // The previous ball position, prepended to the trail on the next tick so the
  // fade traces the ball's path between ticks (not just disconnected old dots).
  const lastBallRef = useRef<BallPoint | null>(null);
  const actionsRef = useRef<LiveAction[]>([]);
  // A "we are intentionally tearing down" flag so onclose does not reconnect.
  const intentionalCloseRef = useRef(false);
  // The eventId that landed a TERMINAL error (not_tracked / auth_failed /
  // subscription_required / limit …). Sticky across the ~30-min token refetch so
  // we do not re-subscribe and re-fail every refresh for a genuinely untracked
  // match. Cleared implicitly when the eventId changes.
  const terminalEventRef = useRef<number | null>(null);
  // The feed source ('basic' | 'full'); 'action' frames are only honoured on the
  // FULL feed (the basic feed must never mutate lastAction/actions/score here).
  const sourceRef = useRef<'basic' | 'full' | null>(null);
  // A monotonic counter bumped on EVERY inbound frame; the ping loop snapshots it
  // each tick to detect a dead (half-open) socket that no longer delivers frames.
  const frameCounterRef = useRef(0);
  const lastFrameAtRef = useRef(0);
  const staleTicksRef = useRef(0);

  // --- Real-time goal / red-card alert dedupe -----------------------------
  // The last score we have ALREADY notified for (or that arrived in the initial
  // snapshot). A goal fires only when a later score strictly exceeds this, so the
  // `subscribed` snapshot / a replayed `event` frame never fires an alert.
  const notifiedScoreRef = useRef<{ home: number; away: number } | null>(null);
  // True once we've ingested the subscribed snapshot for THIS event, so we know a
  // later increment is genuine (not the first score we ever saw).
  const scoreBaselineSetRef = useRef(false);
  // The id of the last red-card action we alerted on, so the same card (re-sent on
  // a reconnect/replay) never double-fires.
  const notifiedRedCardIdRef = useRef<string | null>(null);
  // Monotonic id for the in-app celebratory flash (LiveGoalEvent.id).
  const goalEventSeqRef = useRef(0);
  // The eventId the alert dedupe is currently scoped to; lets us reset the dedupe
  // on a genuine event change but NOT on a token-refresh reconnect.
  const alertEventRef = useRef<number | null>(null);
  // Stable, render-free mirror of the team short names for the alert body. Kept
  // in sync via an effect (not during render) so the goal hot path reads the
  // latest labels without re-creating the frame handler.
  const teamNamesRef = useRef<{ home?: string; away?: string }>({});
  useEffect(() => {
    teamNamesRef.current = { home: homeShortName, away: awayShortName };
  }, [homeShortName, awayShortName]);
  // Same render-free mirror for the fixture (route) UUID, threaded into the alert
  // data so a tap deep-links to /match/<id>.
  const fixtureIdRef = useRef<string | null>(null);
  useEffect(() => {
    fixtureIdRef.current = fixtureId ?? null;
  }, [fixtureId]);

  const safeSetState = useCallback((updater: (prev: LiveBallState) => LiveBallState) => {
    if (mountedRef.current) setState(updater);
  }, []);

  /**
   * Compare a freshly-received score against the last one we have a baseline for.
   * On a GENUINE later increment (home OR away strictly up), fire the local goal
   * notification, bump the in-app flash event, and advance the dedupe ref. Does
   * NOTHING for the initial snapshot (no baseline yet), an unchanged score, or a
   * score that went down (a provider correction). `scorer` is the latest action's
   * player when the action feed supplied one. Returns the LiveGoalEvent to merge
   * into state, or null when nothing fired.
   */
  const detectGoal = useCallback(
    (next: { home: number; away: number }, scorer: string | null): LiveGoalEvent | null => {
      const prev = notifiedScoreRef.current;
      // No baseline yet → this is the snapshot; record it, never alert.
      if (prev === null || !scoreBaselineSetRef.current) {
        notifiedScoreRef.current = next;
        scoreBaselineSetRef.current = true;
        return null;
      }
      const homeUp = next.home > prev.home;
      const awayUp = next.away > prev.away;
      if (!homeUp && !awayUp) {
        // Unchanged or a downward correction — keep the baseline in sync but quiet.
        if (next.home !== prev.home || next.away !== prev.away) notifiedScoreRef.current = next;
        return null;
      }
      notifiedScoreRef.current = next;
      const team: 'home' | 'away' | null = homeUp && !awayUp ? 'home' : awayUp && !homeUp ? 'away' : null;
      const names = teamNamesRef.current;
      void notifyGoal({
        homeShort: names.home ?? 'Home',
        awayShort: names.away ?? 'Away',
        home: next.home,
        away: next.away,
        scorer,
        fixtureId: fixtureIdRef.current,
      });
      goalEventSeqRef.current += 1;
      return { id: goalEventSeqRef.current, kind: 'goal', team, player: scorer };
    },
    [],
  );

  /**
   * Fire a red-card alert for a freshly-seen red_card action, deduped by action
   * id so a replayed frame never double-fires. Returns the LiveGoalEvent for the
   * flash, or null when it was already alerted / not a red card.
   */
  const detectRedCard = useCallback((action: LiveAction): LiveGoalEvent | null => {
    if (action.action_type !== 'red_card') return null;
    if (notifiedRedCardIdRef.current === action.id) return null;
    notifiedRedCardIdRef.current = action.id;
    void notifyRedCard({ player: action.player, fixtureId: fixtureIdRef.current });
    goalEventSeqRef.current += 1;
    return { id: goalEventSeqRef.current, kind: 'red_card', team: action.team, player: action.player };
  }, []);

  const clearTimers = useCallback(() => {
    if (pingTimerRef.current !== null) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  /** Fully tear down the socket: stop timers, unsubscribe, close. */
  const teardown = useCallback(
    (sendUnsubscribe: boolean) => {
      intentionalCloseRef.current = true;
      clearTimers();
      const socket = socketRef.current;
      socketRef.current = null;
      if (socket) {
        if (socket.readyState === WebSocket.CONNECTING) {
          // Half-open socket: closing now is a no-op (the native socket finishes
          // connecting and leaks). Defer the close to onopen so it is actually
          // torn down, and silence every other handler in the meantime.
          socket.onmessage = null;
          socket.onerror = null;
          socket.onclose = null;
          socket.onopen = () => {
            try {
              socket.close();
            } catch {
              // Already gone.
            }
          };
        } else {
          try {
            if (sendUnsubscribe && socket.readyState === WebSocket.OPEN && eventId !== null) {
              socket.send(JSON.stringify({ action: 'unsubscribe', event_id: eventId }));
            }
          } catch {
            // The socket may already be closing — nothing to do.
          }
          // Detach handlers first so the imminent close does not schedule a reconnect.
          socket.onopen = null;
          socket.onmessage = null;
          socket.onerror = null;
          socket.onclose = null;
          try {
            socket.close();
          } catch {
            // Already closed.
          }
        }
      }
    },
    [clearTimers, eventId],
  );

  // The single frame handler. Defined inside the connect effect so it closes
  // over the current eventId; declared here as a ref-free local for clarity.
  const handleFrame = useCallback(
    (raw: string) => {
      let frame: Record<string, unknown>;
      try {
        frame = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        return; // Non-JSON noise — ignore.
      }
      // Mark liveness: every valid inbound frame advances the counter the ping
      // loop watches for dead-socket detection.
      frameCounterRef.current += 1;
      const type = typeof frame.type === 'string' ? frame.type : '';

      switch (type) {
        case 'subscribed': {
          reconnectAttemptRef.current = 0;
          const source =
            frame.source === 'full' ? 'full' : frame.source === 'basic' ? 'basic' : 'basic';
          sourceRef.current = source;
          const event = (frame.event ?? null) as Record<string, unknown> | null;
          const liveData = Array.isArray(frame.livedata) ? frame.livedata : [];
          const lastLive = liveData.length
            ? (liveData[liveData.length - 1] as Record<string, unknown>)
            : null;
          const ball = lastLive ? readBall(lastLive) : null;

          // Seed the trail newest→oldest. Start with the latest frame's own
          // intra-frame trajectory (the earlier `coordinates` points), then fall
          // back to the per-frame ball positions of the preceding snapshot frames
          // until the cap is reached.
          const seededTrail: BallPoint[] = lastLive ? readBallTrajectory(lastLive) : [];
          for (
            let i = liveData.length - 2;
            i >= 0 && seededTrail.length < TRAIL_CAP;
            i -= 1
          ) {
            const point = readBall(liveData[i] as Record<string, unknown>);
            if (point) seededTrail.push({ x: point.x, y: point.y });
          }
          seededTrail.length = Math.min(seededTrail.length, TRAIL_CAP);
          trailRef.current = seededTrail;
          lastBallRef.current = ball ? { x: ball.x, y: ball.y } : null;
          actionsRef.current = [];

          // Establish (or refresh) the goal/red-card alert BASELINE from this
          // snapshot so a later increment is detected as a genuine goal and the
          // snapshot itself (or a reconnect replay) never fires an alert.
          const snapshotScore = event ? readScore(event) : { home: 0, away: 0 };
          notifiedScoreRef.current = snapshotScore;
          scoreBaselineSetRef.current = true;
          notifiedRedCardIdRef.current = null;

          safeSetState((prev) => ({
            ...prev,
            status: 'live',
            source,
            ball,
            trail: seededTrail,
            score: event ? readScore(event) : prev.score,
            time: event ? readTime(event) : prev.time,
            stats: event ? readStats((event as { stats?: unknown }).stats) : prev.stats,
            lastAction: null,
            actions: [],
            // A fresh subscription clears any stale celebratory flash.
            goalEvent: null,
          }));
          return;
        }

        case 'event': {
          const nextScore = readScore(frame);
          // A goal can surface on either feed as a score increment in an `event`
          // frame. The frame carries no scorer; on the full feed reuse the most
          // recent goal action's player so the alert can still name the scorer (on
          // the basic feed there's no action stream, so this is simply null).
          const recentGoal = actionsRef.current.find((a) => a.action_type === 'goal');
          const goalEvent = detectGoal(nextScore, recentGoal?.player ?? null);
          safeSetState((prev) => ({
            ...prev,
            status: 'live',
            score: nextScore,
            time: readTime(frame),
            stats: readStats((frame as { stats?: unknown }).stats),
            goalEvent: goalEvent ?? prev.goalEvent,
          }));
          return;
        }

        case 'livedata': {
          const ball = readBall(frame);
          if (!ball) return;
          // Build the trail newest→oldest so it traces the ball's PATH between
          // ticks: the frame's own intra-frame trajectory (already newest-first),
          // then where the ball just was, then the prior trail. The current ball
          // is drawn separately and is NOT part of the trail.
          const trajectory = readBallTrajectory(frame);
          const prevBall = lastBallRef.current ? [lastBallRef.current] : [];
          const nextTrail = [...trajectory, ...prevBall, ...trailRef.current].slice(0, TRAIL_CAP);
          trailRef.current = nextTrail;
          lastBallRef.current = { x: ball.x, y: ball.y };
          safeSetState((prev) => ({
            ...prev,
            status: 'live',
            ball,
            trail: nextTrail,
          }));
          return;
        }

        case 'action': {
          // Actions are a FULL-feed concept only; ignore on the basic feed.
          if (sourceRef.current !== 'full') return;
          const action = readAction(frame);
          if (!action) return;
          const nextActions = [action, ...actionsRef.current].slice(0, ACTIONS_CAP);
          actionsRef.current = nextActions;
          // The score may advance on a goal action — keep it in sync if present.
          const nextScore =
            frame.score && typeof frame.score === 'object' ? readScore(frame) : undefined;

          // Real-time alerts (full feed): a goal action names the scorer AND carries
          // the new score → fire the goal alert keyed off the increment; a red_card
          // fires its own alert. Both feed the celebratory in-app flash. detectGoal
          // / detectRedCard own the dedupe so a replay never double-fires.
          let goalEvent: LiveGoalEvent | null = null;
          if (nextScore) {
            goalEvent = detectGoal(
              nextScore,
              action.action_type === 'goal' ? action.player : null,
            );
          }
          const redEvent = detectRedCard(action);
          const flash = goalEvent ?? redEvent;

          safeSetState((prev) => ({
            ...prev,
            status: 'live',
            lastAction: action,
            actions: nextActions,
            score: nextScore ?? prev.score,
            goalEvent: flash ?? prev.goalEvent,
          }));
          return;
        }

        case 'error': {
          const code = typeof frame.code === 'string' ? frame.code : '';
          const status = ERROR_CODE_TO_STATUS[code] ?? 'error';
          // not_tracked / auth_failed / subscription_required / limit are
          // TERMINAL for this event: stop reconnecting AND remember the eventId so
          // the ~30-min token refetch (which re-runs the connect effect) does not
          // re-subscribe and re-fail. Cleared only when the eventId changes.
          terminalEventRef.current = eventId;
          intentionalCloseRef.current = true;
          clearTimers();
          safeSetState((prev) => ({ ...prev, status }));
          return;
        }

        case 'pong':
        case 'odds':
        case 'odds_book':
        default:
          // Keepalive ack, betting noise, ingest_debug and any future/unknown
          // frame: ignore silently.
          return;
      }
    },
    [safeSetState, clearTimers, eventId, detectGoal, detectRedCard],
  );

  // --- Connection lifecycle -------------------------------------------------
  useEffect(() => {
    mountedRef.current = true;

    if (!active) {
      // Disabled or no event: ensure we are torn down and idle.
      teardown(true);
      safeSetState(() => INITIAL_STATE);
      return () => {
        mountedRef.current = false;
      };
    }

    if (!token) {
      // Waiting on (or failed to mint) the credential.
      if (tokenQuery.isError) {
        safeSetState((prev) => ({ ...prev, status: 'error' }));
      } else {
        safeSetState((prev) => ({
          ...prev,
          status: prev.status === 'live' ? 'live' : 'connecting',
        }));
      }
      return () => {
        mountedRef.current = false;
      };
    }

    // STICKY TERMINAL GUARD. If this exact eventId already hit a terminal error
    // (not_tracked / auth_failed / subscription_required / limit), do NOT
    // reconnect on the ~30-min token refetch that re-runs this effect — that
    // would re-subscribe and re-fail forever. The terminal flag is event-scoped:
    // when eventId actually changes the condition is false and we proceed
    // normally (which also re-arms tracking for the new match).
    if (terminalEventRef.current === eventId) {
      teardown(false);
      return () => {
        mountedRef.current = false;
      };
    }

    // A new (or non-terminal) event: drop any stale terminal marker and reset the
    // dead-socket liveness counters for this fresh connection.
    terminalEventRef.current = null;
    frameCounterRef.current = 0;
    lastFrameAtRef.current = 0;
    staleTicksRef.current = 0;
    trailRef.current = [];
    lastBallRef.current = null;
    // Clear the goal/red-card alert dedupe ONLY when the watched event actually
    // changes (not on the ~30-min token refetch that also re-runs this effect).
    // A reconnect re-runs the `subscribed` handler, which re-seeds the baseline
    // from the live snapshot, so an in-flight match never re-fires alerts.
    if (alertEventRef.current !== eventId) {
      alertEventRef.current = eventId;
      notifiedScoreRef.current = null;
      scoreBaselineSetRef.current = false;
      notifiedRedCardIdRef.current = null;
    }

    let cancelled = false;

    const connect = () => {
      if (cancelled || !mountedRef.current) return;
      intentionalCloseRef.current = false;

      let socket: WebSocket;
      try {
        socket = new WebSocket(`${token.ws_url}?token=${token.token}`);
      } catch {
        scheduleReconnect();
        return;
      }
      socketRef.current = socket;

      socket.onopen = () => {
        if (cancelled || !mountedRef.current) return;
        try {
          socket.send(JSON.stringify({ action: 'subscribe', event_id: eventId }));
        } catch {
          // If the subscribe write fails the socket will close → reconnect path.
          return;
        }
        // Start the keepalive ping loop. Seed the liveness snapshot so the first
        // tick measures frames received since the socket opened.
        lastFrameAtRef.current = frameCounterRef.current;
        staleTicksRef.current = 0;
        if (pingTimerRef.current !== null) clearInterval(pingTimerRef.current);
        pingTimerRef.current = setInterval(() => {
          if (cancelled || !mountedRef.current) return;
          const sock = socketRef.current;
          if (!sock || sock.readyState !== WebSocket.OPEN) return;

          // Dead-socket detection: if no inbound frame has arrived since the last
          // tick, count a stale tick. After DEAD_FRAME_TICKS consecutive stale
          // ticks the socket is a half-open zombie (ping is being swallowed) →
          // tear it down and reconnect rather than waiting on a close that the
          // OS may never deliver.
          if (frameCounterRef.current === lastFrameAtRef.current) {
            staleTicksRef.current += 1;
            if (staleTicksRef.current >= DEAD_FRAME_TICKS) {
              teardown(false);
              scheduleReconnect();
              return;
            }
          } else {
            staleTicksRef.current = 0;
          }
          lastFrameAtRef.current = frameCounterRef.current;

          try {
            sock.send(JSON.stringify({ action: 'ping' }));
          } catch {
            // Ignore — a dead socket will surface via onclose / the next tick.
          }
        }, PING_INTERVAL_MS);
      };

      socket.onmessage = (ev: WebSocketMessageEvent) => {
        if (cancelled || !mountedRef.current) return;
        if (typeof ev.data === 'string') handleFrame(ev.data);
      };

      socket.onerror = () => {
        // Surface as reconnecting; the imminent onclose drives the retry.
        if (!intentionalCloseRef.current) {
          safeSetState((prev) =>
            prev.status === 'not_tracked' ? prev : { ...prev, status: 'reconnecting' },
          );
        }
      };

      socket.onclose = () => {
        if (pingTimerRef.current !== null) {
          clearInterval(pingTimerRef.current);
          pingTimerRef.current = null;
        }
        if (cancelled || !mountedRef.current || intentionalCloseRef.current) return;
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (cancelled || !mountedRef.current) return;
      const attempt = reconnectAttemptRef.current;
      // Reconnect ceiling: a permanently-down feed must not spin a forever 30s
      // retry storm (battery/radio drain). After the cap, surface the graceful
      // error UI and stop scheduling.
      if (attempt >= MAX_RECONNECT_ATTEMPTS) {
        safeSetState((prev) => ({ ...prev, status: 'error' }));
        return;
      }
      reconnectAttemptRef.current = attempt + 1;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
      safeSetState((prev) =>
        prev.status === 'not_tracked' ? prev : { ...prev, status: 'reconnecting' },
      );
      if (reconnectTimerRef.current !== null) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    safeSetState((prev) => ({
      ...prev,
      status: prev.status === 'live' ? 'live' : 'connecting',
    }));
    reconnectAttemptRef.current = 0;
    connect();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      teardown(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, token, eventId, tokenQuery.isError]);

  return state;
}
